import { memo, ReactNode, useCallback, useMemo } from 'react'

import { PartnerHierarchyType } from 'clickNgo/entities/partner/configs'
import { IPartner, IPartnerFlags } from 'clickNgo/entities/partner/models'
import { Translation } from 'cng-common/features/translations'
import { Tooltip } from 'core-components/library/tooltip'
import { getFormatDate } from 'core/classes/helpers/DateHelper'

import { CockpitCardHeader } from '../../../../../components/cockpitCardHeader'
import { companyDetailsTranslations } from '../../translation'

import { AMPLYFY_PROGRAM, EXTENDED_ATTR_SECOND } from './configs'
import { checkBlackListFlag, getPartnerTypeDescription } from './helpers'

import styles from './CompanyDetailsHeader.module.scss'

type Props = {
  partner: IPartner
  partnerFlags: IPartnerFlags
  /** Stands at the right end of the header, e.g. the partner's actions and the drawer's close. */
  actions?: ReactNode
}

/** The partner's header, worn like a tile's: the company as the title, and under it what kind of
 *  partner it is: partner type, HQ or Branch, the year it joined, and a red pill when it is not
 *  addressable. */
export const CompanyDetailsHeader = memo<Props>(({ partner, partnerFlags, actions }) => {
  const { communicationName, hierarchyType, partnerType, partnerTypeStartDate } = partner
  const partnerTypeDescription = getPartnerTypeDescription(partnerType)
  const bpNotAddressable = useMemo(() => {
    const isAmplifyProgram = AMPLYFY_PROGRAM.includes(partnerType)
    const isBlackListFlag = checkBlackListFlag(partnerFlags)

    return !isAmplifyProgram && isBlackListFlag ? EXTENDED_ATTR_SECOND : ''
  }, [partnerFlags, partnerType])
  const isHQ = hierarchyType === PartnerHierarchyType.HEADQUARTERS

  const companyNameTip = useCallback(() => <>{communicationName}</>, [communicationName])

  const partnerTypeDescriptionTip = useCallback(
    () => <>{partnerTypeDescription}</>,
    [partnerTypeDescription]
  )

  const hpMemberUnderAmplifyStartDate = useMemo(
    () => (partnerTypeStartDate ? getFormatDate(new Date(partnerTypeStartDate), 'date2') : ''),
    [partnerTypeStartDate]
  )

  const hpMemberUnderAmplifyStartYear = useMemo(
    () =>
      partnerTypeStartDate ? getFormatDate(new Date(partnerTypeStartDate), 'custom', 'YYYY') : '',
    [partnerTypeStartDate]
  )

  const hpMemberUnderAmplifyTip = useCallback(
    () => (
      <span>
        <Translation text={companyDetailsTranslations.header.hpMemberUnderAmplifySince} />
        {hpMemberUnderAmplifyStartDate}
      </span>
    ),
    [hpMemberUnderAmplifyStartDate]
  )

  return (
    <div className={styles.root}>
      <CockpitCardHeader
        title={
          <Tooltip tipContent={companyNameTip}>
            <span className={styles.name}>{communicationName}</span>
          </Tooltip>
        }
        description={
          <>
            <Tooltip
              className={styles.shrink}
              disabled={!partnerTypeDescription}
              tipContent={partnerTypeDescriptionTip}
            >
              <span className={styles.context}>{partnerType}</span>
            </Tooltip>
            <span className={styles.separator} aria-hidden>
              &middot;
            </span>
            <span className={styles.context}>{isHQ ? 'HQ' : 'Branch'}</span>
            {hpMemberUnderAmplifyStartYear && (
              <>
                <span className={styles.separator} aria-hidden>
                  &middot;
                </span>
                <Tooltip tipContent={hpMemberUnderAmplifyTip}>
                  <span className={styles.context}>Since {hpMemberUnderAmplifyStartYear}</span>
                </Tooltip>
              </>
            )}
            {bpNotAddressable && <span className={styles.pill}>{bpNotAddressable}</span>}
          </>
        }
        actions={actions}
      />
    </div>
  )
})
